/**
 * Presentational "Exclude this case" form. Owns FORM state only — the container (`ExcludeAction`) owns
 * open/close, the `excludeCase` call, the submit state machine, the `loggedAs` snapshot and the gate.
 */
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Textarea
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type { ExcludePayload } from '../../interface/exclude';
import {
  buildExcludePayload,
  type ExcludeFormValues,
  type ExcludeValidationError
} from '../../slots/buildExcludePayload';
import { splitReasonOptions, toSelectedReasons } from '../../slots/splitReasonOptions';
import {
  EXCLUDE_COLORS,
  EXCLUDE_GLYPH_PX,
  EXCLUDE_SIZES,
  CASE_ACTION_RADIUS
} from '../../styles/tokens';
import { RoundedButton } from '@codametrix/ui-components';
import { LoggedAsLine } from '../CaseActionModal';
import { ReasonField, useReasonOptions } from '../ReasonField';
import { ExcludeGlyph, WarningTriangleGlyph } from './ExcludeGlyph';

/**
 * The design-system button atom, re-typed with EXACTLY the extra props used here and no more.
 * `RoundedButton` maps a spectre `className` to a theme variant and spreads everything else onto the
 * underlying Chakra `Button` at runtime, but its own signature is only `ButtonHTMLAttributes`. Typing this
 * as `React.FC<ButtonProps>` would be worse, not better: it would advertise Chakra's entire style-prop
 * surface including `variant`, which `RoundedButton` overwrites from `className`.
 */
const AtomButton = RoundedButton as React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    isLoading?: boolean;
    bg?: string;
    color?: string;
    _hover?: { bg?: string };
  }
>;

export type ExcludeSubmitState = 'idle' | 'excluding' | 'error';

export interface ExcludeModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly loggedAs: {
    readonly name: string;
    readonly at: Date;
    /** IANA zone -- pins the footer timestamp for deterministic stories/tests. */
    readonly timeZone?: string;
  };
  readonly onSubmit: (payload: ExcludePayload) => Promise<void>;
  readonly submitState: ExcludeSubmitState;
  readonly errorMessage?: string;
  /** Chakra restores focus here on close (the container passes its trigger ref). */
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
}

/** Dialog title -- the SAME constant names the visible `<h2>`, the dialog's `aria-label`, and (imported by
 *  `ExcludeAction`) the trigger button, so the three cannot drift. */
export const EXCLUDE_MODAL_TITLE = 'Exclude this case';

/** Description text. Deliberately says nothing about archiving: the mock's "…and archives it" asserts the
 *  CASE is archived, and a backend audit established there is no case-archive capability at all. */
const EXCLUDE_MODAL_SUBTITLE =
  'Excluding this case removes it from your active workflow.';

const WARNING_ID = 'exclude-warning';

/** Shared eyebrow-label style, mirroring HoldModal's. Uppercase is written literally into the strings so it
 *  cannot cascade into the child "required"/"optional" hints, which stay normal case. */
const EYEBROW = {
  textStyle: 'Subtitle 1',
  color: EXCLUDE_COLORS.inkSoft
} as const;

export const ExcludeModal: React.FC<ExcludeModalProps> = ({
  isOpen,
  onClose,
  loggedAs,
  onSubmit,
  submitState,
  errorMessage,
  finalFocusRef
}) => {
  const [reasonKeys, setReasonKeys] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [caseContext, setCaseContext] = useState('');
  /**
   * Null until the first submit attempt, so nothing renders as an error before the user has submitted.
   * `at` drives the live error derivation; `focus` records whether to move focus to the reason group for
   * THAT attempt, decided where the validation result is already known. A fresh object per attempt makes
   * it a correct effect dependency.
   */
  const [attempt_, setAttempt] = useState<{
    at: Date;
    focus: 'reason' | null;
  } | null>(null);
  const submittedAt = attempt_?.at ?? null;

  const reasonGroupRef = useRef<HTMLElement>(null);
  /** Synchronous activation latch -- see `attempt()` for why the `busy` prop alone is not sufficient. */
  const submittingRef = useRef(false);

  // Defence in depth: the container also remounts this component per open via a key. Both exist because a
  // stale reason or note surviving into a NEW exclusion would be a data-integrity bug, not a cosmetic one.
  useEffect(() => {
    if (isOpen) {
      setReasonKeys([]);
      setNote('');
      setCaseContext('');
      setAttempt(null);
    }
  }, [isOpen]);

  const { state: reasonState, retry } = useReasonOptions('exclude', isOpen);
  const options = reasonState.status === 'loaded' ? reasonState.options : [];
  const noReasons =
    reasonState.status !== 'loaded' ||
    splitReasonOptions(options).pills.length + splitReasonOptions(options).listOptions.length === 0;

  const busy = submitState === 'excluding';

  const values: ExcludeFormValues = {
    reasons: toSelectedReasons(options, reasonKeys),
    note,
    caseContext
  };

  // Live-derived every render against the CURRENT values but the FIXED `submittedAt`, so editing a field
  // clears its error immediately and nothing flickers from an unrelated re-render.
  const liveResult = submittedAt ? buildExcludePayload(values) : null;
  const errs: ExcludeValidationError[] =
    liveResult && liveResult.ok === false ? liveResult.errors : [];
  const reasonError = errs.includes('reason-required');

  // Focus the reason group AFTER React commits, not synchronously in the click handler. React 18 batches,
  // so a synchronous focus would land while the error node, `aria-invalid` and `aria-describedby` do not yet
  // exist -- and assistive tech computes an element's description at focus time and does not re-read it.
  useEffect(() => {
    if (!attempt_?.focus) return;
    reasonGroupRef.current?.focus();
  }, [attempt_]);

  const attempt = async (): Promise<void> => {
    // TWO guards, because one is not enough. `busy` is a render-time PROP: the container sets
    // `submitState:'excluding'` inside `onSubmit`, but this component does not see it until that state
    // update commits. Two clicks landing in the same render both read `busy === false` and both call
    // `onSubmit`. The container's own `inFlightRef` then stops the second request reaching the api -- but
    // the UI had already been corrupted, showing "An exclusion is already in progress." and re-enabling the
    // form while the first request was still outstanding. `submittingRef` is synchronous, so the second
    // click cannot get past this line at all.
    if (submittingRef.current || busy) return;
    const result = buildExcludePayload(values);
    setAttempt({ at: new Date(), focus: result.ok === false ? 'reason' : null });
    // `result.ok === false`, never `!result.ok`: under this repo's `strict: false` the negation does not
    // narrow the union.
    if (result.ok === false) return; // errors render below; the buttons stay enabled so they can announce
    submittingRef.current = true;
    try {
      await onSubmit(result.payload);
    } finally {
      // Cleared even on a throw, so a rejected submit does not wedge the form permanently closed. The
      // container owns whether a RETRY is allowed (it locks after an unknown outcome); this ref only
      // serializes activation.
      submittingRef.current = false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      finalFocusRef={finalFocusRef}
    >
      <ModalOverlay />
      <ModalContent
        data-testid="exclude-modal"
        borderRadius={CASE_ACTION_RADIUS.modal}
        maxW="52rem"
        aria-label={EXCLUDE_MODAL_TITLE}
        aria-busy={busy || undefined}
      >
        <Flex
          align="center"
          gap={3}
          px={7}
          pt={6}
          pb={4}
          borderBottomWidth="1px"
          borderColor={EXCLUDE_COLORS.border}
        >
          <Box
            aria-hidden
            data-testid="exclude-modal-icon"
            bg={EXCLUDE_COLORS.iconBg}
            color={EXCLUDE_COLORS.iconStroke}
            borderRadius={CASE_ACTION_RADIUS.tile}
            boxSize={EXCLUDE_SIZES.headerTile}
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ExcludeGlyph size={EXCLUDE_GLYPH_PX.header} />
          </Box>
          {/* `as="h2"` so heading navigation inside the dialog finds the title; Chakra's default emits a
              paragraph. `textStyle` keeps the visual size unchanged. */}
          <Text as="h2" textStyle="h3">
            {EXCLUDE_MODAL_TITLE}
          </Text>
        </Flex>
        <ModalCloseButton
          aria-label="Close"
          top="2.125rem"
          right={7}
          bg={EXCLUDE_COLORS.inputBg}
          border={`1px solid ${EXCLUDE_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.small}
          size="sm"
          color={EXCLUDE_COLORS.inkMuted}
          _hover={{ bg: EXCLUDE_COLORS.closeHoverBg }}
        />

        {/* THE ONLY `ModalBody`. Chakra wires the dialog's `aria-describedby` to it, so both the subtitle and
            the warning must live here -- the warning is the sole place the product states that exclusion is
            irreversible, and a user who never encounters it before the submit button has not been warned. */}
        <ModalBody px={7} pt={4} pb={0}>
          <Text textStyle="Body 1" color={EXCLUDE_COLORS.inkMuted} mb={4}>
            {EXCLUDE_MODAL_SUBTITLE}
          </Text>
          {/* No `role="alert"` and no `aria-live`: this is present from first paint, and the ARIA Alert
              pattern is for time-sensitive DYNAMIC content. Announcing it as an alert on open would be false
              urgency and would compete with the real validation alert below. It reaches the user through the
              dialog description instead, and again through the submit button's `aria-describedby`. */}
          <Flex
            id={WARNING_ID}
            data-testid="exclude-warning"
            gap={3}
            align="flex-start"
            bg={EXCLUDE_COLORS.warnBg}
            border={`1px solid ${EXCLUDE_COLORS.warnBorder}`}
            borderRadius={CASE_ACTION_RADIUS.control}
            color={EXCLUDE_COLORS.warnInk}
            px={4}
            py={3}
          >
            <Box flexShrink={0} mt="0.125rem">
              <WarningTriangleGlyph
                size={EXCLUDE_GLYPH_PX.warn}
                counterFill={EXCLUDE_COLORS.warnBg}
              />
            </Box>
            <Text textStyle="Body 1">
              <Text as="b">Heavy action.</Text> Once excluded, the case is
              removed from all coder queues and only a{' '}
              <Text as="b">Coding Manager</Text> can restore it.
            </Text>
          </Flex>
        </ModalBody>

        {/* The form renders OUTSIDE ModalBody so it does not become part of the dialog's accessible
            description -- the same trap HoldModal documents. */}
        <Box px={7} pt={5} pb={5} maxH="52vh" overflowY="auto">
          <Box
            as="fieldset"
            ref={reasonGroupRef}
            tabIndex={-1}
            aria-labelledby="exclude-reason-legend"
            aria-invalid={reasonError || undefined}
            aria-describedby={reasonError ? 'exclude-reason-error' : undefined}
            mb={6}
          >
            {/* `display="block"` overrides the UA `legend { display: table }`, which wraps inline content in
                anonymous table cells and swallows the whitespace between the spans. Keeping a real
                `<legend>` (rather than only an `aria-label`) is what associates the VISIBLE eyebrow with the
                group. */}
            <Text
              as="legend"
              id="exclude-reason-legend"
              {...EYEBROW}
              display="block"
              mb={3}
            >
              REASON{' '}
              <Text as="span" color={EXCLUDE_COLORS.error}>
                required
              </Text>{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                select all that apply
              </Text>
            </Text>
            <ReasonField
              kind="exclude"
              idPrefix="exclude"
              mode="multi"
              state={reasonState}
              onRetry={retry}
              selectedKeys={reasonKeys}
              onChange={setReasonKeys}
              isDisabled={busy}
              palette={{
                chipSelected: EXCLUDE_COLORS.chipSelected,
                border: EXCLUDE_COLORS.border,
                inkSoft: EXCLUDE_COLORS.inkSoft,
                inkMuted: EXCLUDE_COLORS.inkFaint,
                error: EXCLUDE_COLORS.error
              }}
            />
            {/* Stays MOUNTED once submitted, with its text toggled, so toggling the last reason off and on
                does not remount the live region and re-announce on every click. */}
            {submittedAt && (
              <Text
                data-testid="exclude-reason-error"
                id="exclude-reason-error"
                role="alert"
                color={EXCLUDE_COLORS.error}
                textStyle="Body 1"
                mt={2}
              >
                {reasonError ? 'Select at least one exclusion reason.' : ''}
              </Text>
            )}
          </Box>

          <Box mb={6}>
            <Text as="label" htmlFor="exclude-note-input" {...EYEBROW}>
              NOTE{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="exclude-note-input"
              data-testid="exclude-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              isDisabled={busy}
              maxLength={1024}
              placeholder="Add details about this exclusion (optional)…"
              bg={EXCLUDE_COLORS.inputBg}
              borderColor={EXCLUDE_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _placeholder={{ color: EXCLUDE_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          <Box>
            <Text as="label" htmlFor="exclude-context-input" {...EYEBROW}>
              ADD CONTEXT FOR THIS CASE{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="exclude-context-input"
              data-testid="exclude-context"
              value={caseContext}
              onChange={(e) => setCaseContext(e.target.value)}
              isDisabled={busy}
              maxLength={2000}
              placeholder="Add context for this case (optional)…"
              bg={EXCLUDE_COLORS.inputBg}
              borderColor={EXCLUDE_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _placeholder={{ color: EXCLUDE_COLORS.inkFaint }}
              mt={1}
            />
          </Box>
        </Box>

        <ModalFooter
          alignItems="center"
          gap={4}
          px={7}
          sx={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          borderTopWidth="1px"
          borderColor={EXCLUDE_COLORS.border}
        >
          <LoggedAsLine
            testId="exclude-logged-as"
            label="Logged as"
            name={loggedAs.name}
            at={loggedAs.at}
            timeZone={loggedAs.timeZone}
          />
          {/* The message STACKS ABOVE the buttons rather than sharing their row. It used to be a sibling of
              both buttons inside one wrapping flex, so the moment it appeared the row overflowed and
              "Exclude and Archive" wrapped to a second line, stranding Cancel beside the message. The button
              row is `flexWrap="nowrap"` so it can never reflow again. */}
          <Flex flex="1" direction="column" align="flex-end" gap={2}>
            {submitState === 'error' && errorMessage && errs.length === 0 && (
              <Text
                data-testid="exclude-submit-error"
                role="alert"
                color={EXCLUDE_COLORS.error}
                textStyle="Body 1"
                textAlign="right"
              >
                {errorMessage}
              </Text>
            )}
            <Flex gap={3} align="center" flexWrap="nowrap">
            <AtomButton
              className="btn-secondary"
              data-testid="exclude-cancel"
              onClick={onClose}
            >
              Cancel
            </AtomButton>
            <AtomButton
              className="btn-primary"
              data-testid="exclude-submit"
              onClick={() => void attempt()}
              aria-disabled={busy || noReasons || undefined}
              // Points at the warning so the risk is announced again at the moment of commitment, not only
              // once on open.
              aria-describedby={WARNING_ID}
              // NOT `isLoading`. Chakra's `isLoading` sets the real `disabled` attribute, which would blur
              // the focused button to `<body>` the instant a submit starts -- the exact focus destruction
              // `aria-disabled` was chosen to avoid. Progress is conveyed by the dialog's `aria-busy` and
              // the container's status region instead; the visual cue is opacity, which costs no semantics.
              style={busy ? { opacity: 0.7, cursor: 'progress' } : undefined}
              bg={EXCLUDE_COLORS.danger}
              color={EXCLUDE_COLORS.onFilled}
              _hover={{ bg: EXCLUDE_COLORS.dangerHover }}
            >
              Exclude and Archive
            </AtomButton>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
